import * as React from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { LEGAL_PUBLISHED, LEGAL_VERSIONS } from '@lib/constants/legal'
import { CONTACT_EMAIL } from '@lib/constants/contact'
import { API_ERROR_CODE } from '@lib/dto/api-error.dto'
import type { SimilarVendorListDto } from '@lib/dto/vendor-signup.dto'
import { VENDOR_SERVICE_LABELS, VENDOR_SERVICES, type VendorService } from '@lib/enums/vendor-service.enum'
import { safeRedirectPath } from '@lib/helpers/safe-redirect'
import { isSitePath } from '@lib/helpers/site-paths'
import { vendorSignupInputSchema, type VendorSignupInput } from '@lib/input/vendor-signup.input'
import { useAuth } from '@/auth/AuthContext'
import { AuthShell } from '@/components/AuthShell'
import { LegalConsent } from '@/components/billing/LegalConsent'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Checkbox } from '@/components/ui/checkbox'
import { Field } from '@/components/ui/field'
import { Input } from '@/components/ui/input'
import { FormError } from '@/components/ui/form-error'
import { isApiError, requestErrorMessage } from '@/helpers/apiClient'
import { authService } from '@/services/AuthService'

const ERROR_FIELDS = [
  'companyName',
  'services',
  'addressLine1',
  'addressLine2',
  'city',
  'state',
  'zip',
  'serviceRadiusMiles',
  'name',
  'email',
  'password',
  'legal',
  'form',
] as const

type ErrorField = (typeof ERROR_FIELDS)[number]
type Errors = Partial<Record<ErrorField, string>>

function isErrorField(value: PropertyKey | undefined): value is ErrorField {
  return ERROR_FIELDS.some((field) => field === value)
}

/** Where a finished signup goes: back to where it came from (a venue's page, say), or the vendor's home. */
function landingFor(next: string | null): { app: string } | { site: string } {
  const safe = safeRedirectPath(next)
  if (safe === null) return { app: '/vendor' }
  return isSitePath(new URL(safe, window.location.origin).pathname) ? { site: safe } : { app: safe }
}

/**
 * A vendor company signing itself up (docs/public-site.spec.md, "Vendor
 * signup"). Free, no checkout. Before it makes a company, it asks whether the
 * company is one already here; "That's us" asks that company to let them in.
 * Until the email is confirmed, the company can't ask venues to work with it
 * or appear in their search, and the app says so at the top of every page.
 */
export function SignupVendorPage() {
  if (!LEGAL_PUBLISHED) {
    return (
      <AuthShell>
        <Card size="lg" className="w-full max-w-md">
          <CardHeader>
            <CardTitle className="text-display">Signup opens soon</CardTitle>
            <CardDescription>
              FLOH Venues isn’t taking new sign-ups yet. Venues and planners can still add your company, and we’ll email you an invitation.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Link to="/login" className="text-sm underline underline-offset-2">
              Sign in
            </Link>
          </CardContent>
        </Card>
      </AuthShell>
    )
  }
  return <SignupVendorForm />
}

function SignupVendorForm() {
  const { vendorSignup } = useAuth()
  const navigate = useNavigate()
  const [params] = useSearchParams()
  const [step, setStep] = React.useState<'details' | 'similar' | 'asked'>('details')
  const [companyName, setCompanyName] = React.useState('')
  const [services, setServices] = React.useState<VendorService[]>([])
  const [addressLine1, setAddressLine1] = React.useState('')
  const [addressLine2, setAddressLine2] = React.useState('')
  const [city, setCity] = React.useState('')
  const [state, setState] = React.useState('')
  const [zip, setZip] = React.useState('')
  const [radius, setRadius] = React.useState('50')
  const [name, setName] = React.useState('')
  const [email, setEmail] = React.useState('')
  const [password, setPassword] = React.useState('')
  const [agreed, setAgreed] = React.useState(false)
  const [errors, setErrors] = React.useState<Errors>({})
  const [similar, setSimilar] = React.useState<SimilarVendorListDto['vendors']>([])
  /** The checked form, kept while "Is this you?" is asked, for "No, we're new". */
  const [ready, setReady] = React.useState<VendorSignupInput | null>(null)
  const [askedCompany, setAskedCompany] = React.useState('')
  const [pendingInvite, setPendingInvite] = React.useState(false)
  const [inviteSent, setInviteSent] = React.useState(false)
  const [busy, setBusy] = React.useState(false)

  /** The form, checked with the API's own rules; null with the messages shown when it isn't right. */
  function checked(): VendorSignupInput | null {
    if (!agreed) {
      setErrors({ legal: 'Agree to the terms and privacy policy to continue.' })
      return null
    }
    const parsed = vendorSignupInputSchema.safeParse({
      companyName,
      services,
      addressLine1,
      addressLine2,
      city,
      state,
      zip,
      serviceRadiusMiles: Number(radius),
      name,
      email,
      password,
      legal: { termsVersion: LEGAL_VERSIONS.terms, privacyVersion: LEGAL_VERSIONS.privacy },
    })
    if (!parsed.success) {
      const next: Errors = {}
      for (const issue of parsed.error.issues) {
        const field = issue.path[0]
        if (isErrorField(field) && next[field] === undefined) next[field] = issue.message
      }
      setErrors(next)
      return null
    }
    setErrors({})
    return parsed.data
  }

  async function create(input: VendorSignupInput): Promise<void> {
    setBusy(true)
    setPendingInvite(false)
    try {
      await vendorSignup(input)
      const landing = landingFor(params.get('next'))
      if ('site' in landing) window.location.assign(landing.site)
      else navigate(landing.app, { replace: true })
    } catch (error) {
      if (isApiError(error) && error.body.code === API_ERROR_CODE.invitePending) setPendingInvite(true)
      setErrors({ form: requestErrorMessage(error) })
      setStep('details')
    } finally {
      setBusy(false)
    }
  }

  async function handleContinue(event: React.FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault()
    const input = checked()
    if (input === null) return
    setBusy(true)
    try {
      const found = await authService.vendorSimilar({ companyName: input.companyName, addressLine1: input.addressLine1, city: input.city, state: input.state, zip: input.zip })
      if (found.vendors.length > 0) {
        setSimilar(found.vendors)
        setReady(input)
        setStep('similar')
        setBusy(false)
        return
      }
    } catch {
      // The check is a courtesy: if it can't run, signup goes ahead.
    }
    await create(input)
  }

  async function askToJoin(vendorId: string, vendorName: string): Promise<void> {
    setBusy(true)
    try {
      await authService.vendorJoin({ vendorId, name, email })
      setAskedCompany(vendorName)
      setStep('asked')
    } catch (error) {
      setErrors({ form: requestErrorMessage(error) })
    } finally {
      setBusy(false)
    }
  }

  async function sendInvitationAgain(): Promise<void> {
    setBusy(true)
    try {
      await authService.requestMagicLink({ email })
      setInviteSent(true)
    } catch (error) {
      setErrors({ form: requestErrorMessage(error) })
    } finally {
      setBusy(false)
    }
  }

  function toggle(service: VendorService, on: boolean): void {
    setServices((current) => (on ? [...current, service] : current.filter((s) => s !== service)))
  }

  if (step === 'asked') {
    return (
      <AuthShell>
        <Card size="lg" className="w-full max-w-lg">
          <CardHeader>
            <CardTitle className="text-display">We’ve asked {askedCompany} to let you in</CardTitle>
            <CardDescription>
              Its owners have an email with your name and address and a button to invite you. Their invitation will come to {email}. If you
              don’t hear back, write to us at {CONTACT_EMAIL}.
            </CardDescription>
          </CardHeader>
        </Card>
      </AuthShell>
    )
  }

  if (step === 'similar') {
    return (
      <AuthShell>
        <Card size="lg" className="w-full max-w-lg">
          <CardHeader>
            <CardTitle className="text-display">Is this you?</CardTitle>
            <CardDescription>These companies near you are already on FLOH Venues. If one is yours, ask to join it rather than starting another.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <ul className="divide-y divide-line rounded-lg border border-line">
              {similar.map((vendor) => (
                <li key={vendor.id} className="flex items-center justify-between gap-3 px-4 py-3">
                  <div>
                    <p className="text-sm font-medium text-ink">{vendor.name}</p>
                    <p className="text-sm text-ink-muted">{[vendor.city, vendor.state].filter((part) => part !== null).join(', ') || 'No address yet'}</p>
                  </div>
                  <Button variant="outline" size="sm" disabled={busy} onClick={() => void askToJoin(vendor.id, vendor.name)}>
                    That’s us
                  </Button>
                </li>
              ))}
            </ul>
            {errors.form ? <FormError>{errors.form}</FormError> : null}
            <div className="flex flex-wrap gap-2">
              <Button pending={busy} disabled={ready === null} onClick={() => (ready === null ? undefined : void create(ready))}>
                No, we’re new
              </Button>
              <Button variant="ghost" disabled={busy} onClick={() => setStep('details')}>
                Back
              </Button>
            </div>
          </CardContent>
        </Card>
      </AuthShell>
    )
  }

  return (
    <AuthShell>
      <Card size="lg" className="w-full max-w-lg">
        <CardHeader>
          <CardTitle className="text-display">Join as a vendor</CardTitle>
          <CardDescription>
            Free for vendors. Your profile is seen by venue staff, planners and couples at the venues you serve, and you can ask venues to work with you.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form className="space-y-6" onSubmit={(e) => void handleContinue(e)} noValidate>
            <fieldset className="space-y-3">
              <legend className="mb-1 text-sm font-semibold text-ink">Your company</legend>
              <Field label="Company name" htmlFor="company-name" error={errors.companyName}>
                <Input id="company-name" value={companyName} onChange={(e) => setCompanyName(e.target.value)} autoComplete="organization" />
              </Field>
              <div className="space-y-2">
                <p className="text-sm font-medium text-ink">What you offer</p>
                <div className="grid grid-cols-2 gap-2">
                  {VENDOR_SERVICES.map((service) => (
                    <label key={service} className="inline-flex items-center gap-2 text-sm text-ink">
                      <Checkbox checked={services.includes(service)} onCheckedChange={(on) => toggle(service, on)} />
                      {VENDOR_SERVICE_LABELS[service]}
                    </label>
                  ))}
                </div>
                {errors.services ? <FormError>{errors.services}</FormError> : null}
              </div>
              <Field label="Street address" htmlFor="address-line-1" error={errors.addressLine1}>
                <Input id="address-line-1" value={addressLine1} onChange={(e) => setAddressLine1(e.target.value)} autoComplete="address-line1" />
              </Field>
              <Field label="Suite or unit" htmlFor="address-line-2" hint="Optional" error={errors.addressLine2}>
                <Input id="address-line-2" value={addressLine2} onChange={(e) => setAddressLine2(e.target.value)} autoComplete="address-line2" />
              </Field>
              <div className="grid grid-cols-[2fr_1fr_1fr] gap-2">
                <Field label="City" htmlFor="city" error={errors.city}>
                  <Input id="city" value={city} onChange={(e) => setCity(e.target.value)} autoComplete="address-level2" />
                </Field>
                <Field label="State" htmlFor="state" error={errors.state}>
                  <Input id="state" value={state} onChange={(e) => setState(e.target.value)} autoComplete="address-level1" />
                </Field>
                <Field label="ZIP" htmlFor="zip" error={errors.zip}>
                  <Input id="zip" value={zip} onChange={(e) => setZip(e.target.value)} autoComplete="postal-code" />
                </Field>
              </div>
              <Field label="How far you travel, in miles" htmlFor="radius" hint="Venues this far from your address are the ones the app suggests." error={errors.serviceRadiusMiles}>
                <Input id="radius" type="number" min={1} max={500} value={radius} onChange={(e) => setRadius(e.target.value)} />
              </Field>
            </fieldset>
            <fieldset className="space-y-3">
              <legend className="mb-1 text-sm font-semibold text-ink">You</legend>
              <Field label="Your name" htmlFor="your-name" error={errors.name}>
                <Input id="your-name" value={name} onChange={(e) => setName(e.target.value)} autoComplete="name" />
              </Field>
              <Field label="Email" htmlFor="email" hint="We’ll send a link to confirm it." error={errors.email}>
                <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} autoComplete="username" />
              </Field>
              <Field label="Password" htmlFor="password" error={errors.password}>
                <Input id="password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="new-password" />
              </Field>
            </fieldset>
            <div className="space-y-1">
              <LegalConsent checked={agreed} onChange={setAgreed} />
              {errors.legal ? <FormError>{errors.legal}</FormError> : null}
            </div>
            {errors.form ? (
              <div className="space-y-2">
                <FormError>{errors.form}</FormError>
                {pendingInvite && !inviteSent ? (
                  <Button type="button" variant="outline" size="sm" pending={busy} onClick={() => void sendInvitationAgain()}>
                    Send the invitation again
                  </Button>
                ) : null}
                {inviteSent ? <p className="text-sm text-ink-muted">If {email} was invited, the invitation is on its way again.</p> : null}
              </div>
            ) : null}
            <Button pending={busy} type="submit" className="w-full">
              Continue
            </Button>
            <p className="text-center text-sm text-ink-muted">
              Already on FLOH Venues?{' '}
              <Link to="/login" className="underline underline-offset-2">
                Sign in
              </Link>
            </p>
          </form>
        </CardContent>
      </Card>
    </AuthShell>
  )
}
